import React from 'react';

import { EditOutlined } from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import Can from '@shared/permissions/Can';
import { Card, Descriptions, Space, Button, Empty, Tag, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';

/**
 * ProductionBomShowView - 通用配方詳情組件
 *
 * @param {Object} props
 * @param {Object} props.productionBom - 配方資料 (loaded by route loader)
 * @param {string} props.role - 角色 (admin 或 operator)
 */
export default function ProductionBomShowView({
  productionBom,
  role = 'admin',
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!productionBom) {
    return (
      <Empty description={t('common.error.no_data')} />
    );
  }

  // 配方原料表格欄位
  const columns = [
    {
      title: t(`shared.production_boms.detail.input_product_name`),
      dataIndex: ['input_product', 'name'],
      key: 'name',
    },
    {
      title: t(`shared.production_boms.detail.input_product_sku`),
      dataIndex: ['input_product', 'sku'],
      key: 'sku',
    },
    {
      title: t(`shared.production_boms.detail.input_inventory_type`),
      dataIndex: 'input_inventory_type',
      key: 'input_inventory_type',
      render: (input_inventory_type) => (
        input_inventory_type ? (
          <Tag color="blue">{input_inventory_type.name}</Tag>
        ) : (
          <Tag color="default">{t(`shared.production_boms.list.default_inventory_type`)}</Tag>
        )
      ),
    },
    {
      title: t(`shared.production_boms.detail.quantity`),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity) => <Tag color="blue">{quantity}</Tag>,
    },
  ];

  const basePath = role === 'operator' ? '/operator' : '/admin';
  const breadcrumbItems = role === 'operator'
    ? ['production', 'production.production_boms', { label: productionBom.name }]
    : ['products', 'products.list', { label: productionBom.name }];

  const headerActions = productionBom.editable && (
    <Can requiredPermission="production_boms:write">
      <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`edit`)}>
        {t('common.button.edit')}
      </Button>
    </Can>
  );

  return (
    <div>
      <ListPageHeader
        basePath={basePath}
        items={breadcrumbItems}
        actions={headerActions}
      />
      {/* 基本資訊 */}
      <Card style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t(`shared.production_boms.detail.name`)}>
            {productionBom.name || '-'}
          </Descriptions.Item>
          {role === 'operator' && productionBom.merchant && (
            <Descriptions.Item label={t(`shared.production_boms.detail.merchant`)}>
              {productionBom.merchant.name || '-'}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t(`shared.production_boms.detail.output_product`)}>
            {productionBom.output_product ? `${productionBom.output_product.sku} - ${productionBom.output_product.name}` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t(`shared.production_boms.detail.output_inventory_type`)}>
            {productionBom.output_inventory_type ? (
              <Tag color="blue">{productionBom.output_inventory_type.name}</Tag>
            ) : (
              <Tag color="default">{t(`shared.production_boms.list.default_inventory_type`)}</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t(`common.field.created_at`)}>
            {productionBom.created_at ? formatDateTime(productionBom.created_at) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 配方原料 */}
      <Card title={t(`shared.production_boms.detail.materials`)}>
        {productionBom.production_bom_items && productionBom.production_bom_items.length > 0 ? (
          <Table
            columns={columns}
            dataSource={productionBom.production_bom_items}
            rowKey="id"
            pagination={false}
          />
        ) : (
          <Empty description={t(`shared.production_boms.detail.no_materials`)} />
        )}
      </Card>
    </div>
  );
}
